// src/pages/touchPoints/timeline/TimelineCanvas.js
import React, { useRef, useState, useEffect, useLayoutEffect, useContext } from 'react';
import { layout } from './layout';
import { metricsOf } from './fixtures/metrics';
import { PlusOutlined } from '@ant-design/icons';
import TouchpointNode from './nodes/TouchpointNode';
import JourneyPanel from './nodes/JourneyPanel';
import AdvancedOnly, { AdvancedEditContext } from './AdvancedOnly';
import { PAD, CARD_FULL, CARD_COLLAPSED, headerHeight, hueIndexOf, hueOf, lighten, rgbaOf } from './dims';
import { collectHorizontals, gappedPath } from './bridge';
import { scrollTargetTop, centerScrollTop, centerScrollLeft, journeyTopScroll } from './scrollTarget';
import './timeline.css';

const COL_W = 300;           // column pitch — wider than the 272px panel so panels don't overlap
const HALF_CARD = 115;       // half the 230px touchpoint card width (its right edge)

export default function TimelineCanvas({ model, showRelated, showMetrics = true, attributeById, productById, focusTouchpointId = null, focusJourneyId = null, rightInset = 0, onEdit = () => {}, onEditJourney = () => {}, onAddAfter = () => {}, onAddBranch = () => {} }) {
  // When hiding related journeys, keep the entry journey the user navigated to
  // (model.rootId). Filtering on j.isRoot breaks when the entry's own segment
  // references an attribute written by one of its touchpoints (real journey 837):
  // the entry isn't isRoot, so the filter yields [] and everything disappears.
  const trimmed = showRelated ? model : { ...model, journeys: model.journeys.filter((j) => j.journeyId === model.rootId) };
  // The hover-revealed add `+`s are absolutely positioned, so an <AdvancedOnly> span would
  // anchor its tooltip at the canvas origin. Gate them inline instead: grey + drop onClick +
  // swap the native `title` (which the browser anchors to the element).
  const advanced = useContext(AdvancedEditContext);
  const addTip = advanced ? undefined : 'Advanced editing access required';
  const gate = advanced ? '' : ' is-advanced-disabled';
  const cardH = showMetrics ? CARD_FULL : CARD_COLLAPSED;
  const journeyOf = (jid) => model.journeys.find((x) => x.journeyId === jid);
  // Header height varies with the rendered content (journey title can wrap to 1-2+
  // lines, segment chips can add a row). Measure the real rendered header per panel
  // and re-layout with it; fall back to a content-based estimate before first paint
  // (and in jsdom, where offsets are 0).
  const panelEls = useRef({});
  const cardEls = useRef({});
  const [measured, setMeasured] = useState({ headers: {}, cards: {} });
  // enable position transitions only after the initial measure-and-relayout settles,
  // so the first-paint correction doesn't animate (only later mutations glide)
  const [animate, setAnimate] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnimate(true), 90); return () => clearTimeout(t); }, []);
  // measure the scroll viewport so a lone journey (related hidden) can be centered
  const wrapEl = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const measure = () => { if (wrapEl.current) setWrapW(wrapEl.current.clientWidth); };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  // drag-to-pan: press the canvas background and drag to scroll both axes at once
  // (native trackpad scroll is one-axis-at-a-time). Starts only on non-interactive
  // targets so card/+ clicks and the panel edit icon still work.
  const panStart = useRef(null);
  const [panning, setPanning] = useState(false);
  const onPanDown = (e) => {
    if (e.button !== 0) return; // primary button only
    // pan from anywhere except the interactive controls (their clicks must still fire);
    // card bodies pan too — cards aren't draggable objects in this prototype
    if (e.target.closest('.tl-add, .tl-panel-edit, .tl-tp-edit, button, a, input')) return;
    const wrap = wrapEl.current; if (!wrap) return;
    e.preventDefault(); // suppress text selection / native drag while panning
    panStart.current = { x: e.clientX, y: e.clientY, left: wrap.scrollLeft, top: wrap.scrollTop };
    wrap.setPointerCapture(e.pointerId);
    setPanning(true);
  };
  const onPanMove = (e) => {
    if (!panStart.current) return;
    const wrap = wrapEl.current; if (!wrap) return;
    wrap.scrollLeft = panStart.current.left - (e.clientX - panStart.current.x);
    wrap.scrollTop = panStart.current.top - (e.clientY - panStart.current.y);
  };
  const onPanEnd = (e) => {
    if (!panStart.current) return;
    panStart.current = null;
    setPanning(false);
    if (wrapEl.current) wrapEl.current.releasePointerCapture(e.pointerId);
  };
  // hover-reveal for the add affordances: hidden until you hover near a card. A short
  // leave delay (and the +'s own hover) bridges the gap as the cursor moves onto a +.
  const [hoverTp, setHoverTp] = useState(null);
  const hoverTimer = useRef(null);
  const enterTp = (tid) => { if (hoverTimer.current) clearTimeout(hoverTimer.current); setHoverTp(tid); };
  const leaveTp = () => { if (hoverTimer.current) clearTimeout(hoverTimer.current); hoverTimer.current = setTimeout(() => setHoverTp(null), 220); };
  const headerHeightOf = (jid) => (measured.headers[jid] > 0 ? measured.headers[jid] : headerHeight(journeyOf(jid)));
  const cardHeightOf = (tp) => (measured.cards[tp.touchpoint_id] > 0 ? measured.cards[tp.touchpoint_id] : cardH);
  const lay = layout(trimmed, { cardH, topPad: PAD, colW: COL_W, pad: PAD, headerHeightOf, cardHeightOf });

  // When related journeys are hidden there's a single column; center it in the
  // viewport by translating the whole content group (bands stay full-width, so the
  // zebra stripes keep spanning the canvas). xShift=0 in the normal multi-column view.
  const singleColumn = !showRelated && lay.columns.length === 1;
  const xShift = singleColumn && wrapW ? Math.max(0, Math.round(wrapW / 2 - (lay.columns[0].x ?? 0))) : 0;

  const firstY = {}; lay.cards.forEach((c) => { firstY[c.journeyId] = Math.min(c.y, firstY[c.journeyId] ?? Infinity); });
  // last card per journey + its (measured) height, so the panel bottom tracks the real card bottom
  const lastY = {}; const lastCardH = {};
  lay.cards.forEach((card) => {
    if (card.y >= (lastY[card.journeyId] ?? -Infinity)) { lastY[card.journeyId] = card.y; lastCardH[card.journeyId] = cardHeightOf(card.touchpoint); }
  });
  const cardById = {}; lay.cards.forEach((c) => { cardById[c.touchpoint.touchpoint_id] = c; });

  // Keep the focused touchpoint (the one being edited) in view as its position changes.
  // Scroll from the card's laid-out y (not scrollIntoView on the live element, whose rect
  // is stale mid CSS-transition — that under-scrolled on up-moves). Keyed on the target y
  // so it re-fires on each move (and once when focus is set); scrolls only when off-screen.
  const focusCard = focusTouchpointId != null ? cardById[focusTouchpointId] : null;
  const focusY = focusCard ? focusCard.y : null;
  useLayoutEffect(() => {
    const wrap = wrapEl.current;
    if (!focusCard || !wrap || typeof wrap.scrollTo !== 'function') return;
    const top = scrollTargetTop({
      cardTop: focusCard.y, cardHeight: cardHeightOf(focusCard.touchpoint),
      viewTop: wrap.scrollTop, viewHeight: wrap.clientHeight,
    });
    if (top != null) wrap.scrollTo({ top, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTouchpointId, focusY]);

  // touchpoints whose answer triggers a sub-journey (primary or secondary trigger of any journey)
  const branchTpIds = new Set();
  model.journeys.forEach((journey) => {
    if (journey.primaryTriggerTpId != null) branchTpIds.add(journey.primaryTriggerTpId);
    (journey.secondaryTriggerTpIds || []).forEach((id) => branchTpIds.add(id));
  });
  // Bottom-anchor the header to its card: the segment bar always sits exactly PAD
  // above the first touchpoint, so each journey box (tab → segment → touchpoint)
  // stays tight. (When parallel entries have different header heights, the slack
  // falls above the shorter header's tab rather than inside its box.)
  const panelTop = (jid) => (firstY[jid] ?? 0) - headerHeightOf(jid) - PAD;
  const panelBottom = (jid) => (lastY[jid] ?? 0) + (lastCardH[jid] ?? cardH) + PAD;

  // On opening a journey editor, scroll to the TOP of that journey (its header + first
  // cards) — a journey panel is tall, so top-align rather than centering its midpoint.
  // Keyed on the panel's top so it re-fires once as layout settles; only scrolls when the
  // top isn't already where we want it, so the user can scroll freely afterward.
  const focusPanelTop = focusJourneyId != null ? panelTop(focusJourneyId) : null;
  useLayoutEffect(() => {
    const wrap = wrapEl.current;
    if (focusJourneyId == null || !wrap || typeof wrap.scrollTo !== 'function') return;
    const top = journeyTopScroll(panelTop(focusJourneyId));
    if (Math.abs(wrap.scrollTop - top) > 2) wrap.scrollTo({ top, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusJourneyId, focusPanelTop]);

  // After paint, read each panel's actual header height (segment-bar bottom within the
  // panel) and each card's actual height, then re-layout if either differs from the
  // estimate. Header/card heights don't depend on position, so the second pass measures
  // the same values and stops (fixed point). In jsdom offsets are 0 → estimates stand.
  useLayoutEffect(() => {
    const headers = {};
    Object.keys(panelEls.current).forEach((jid) => {
      const el = panelEls.current[jid];
      if (!el) return;
      const seg = el.querySelector('.tl-segbar');
      const height = seg ? Math.round(seg.offsetTop + seg.offsetHeight) : 0;
      if (height > 0) headers[jid] = height;
    });
    const cards = {};
    Object.keys(cardEls.current).forEach((tid) => {
      const el = cardEls.current[tid];
      const height = el ? Math.round(el.offsetHeight) : 0;
      if (height > 0) cards[tid] = height;
    });
    const same = (a, b) => {
      const keysA = Object.keys(a); const keysB = Object.keys(b);
      return keysA.length === keysB.length && keysA.every((k) => a[k] === b[k]);
    };
    if (!same(headers, measured.headers) || !same(cards, measured.cards)) setMeasured({ headers, cards });
  });

  // Build connector point-paths (trigger → sub-journey). Same-column children drop
  // straight down; cross-column ones route through the gutter beside the target.
  const connectors = [];
  lay.columns.filter((col) => col.primaryTriggerTpId != null).forEach((col) => {
    const trig = cardById[col.primaryTriggerTpId];
    if (!trig || firstY[col.journeyId] == null) return;
    const target = panelTop(col.journeyId);
    if (Math.abs(trig.x - col.x) < 1) {
      connectors.push({ jid: col.journeyId, pts: [[trig.x, trig.y + cardHeightOf(trig.touchpoint)], [col.x, target]] });
      return;
    }
    const dir = col.x >= trig.x ? 1 : -1;
    const exitX = trig.x + dir * HALF_CARD;
    const exitY = trig.y + (showMetrics ? 90 : 48);
    const gutterX = col.x - dir * (COL_W / 2);
    const elbowY = target - PAD;
    connectors.push({ jid: col.journeyId, pts: [[exitX, exitY], [gutterX, exitY], [gutterX, elbowY], [col.x, elbowY], [col.x, target]] });
  });
  // journey spines (vertical) + the connector paths
  const spines = lay.columns.map((col) => ({ jid: col.journeyId, pts: [[col.x, firstY[col.journeyId] ?? 0], [col.x, lastY[col.journeyId] ?? 0]] }));
  // horizontals pass OVER verticals: break any vertical (spine or connector) they cross
  const horizontals = collectHorizontals(connectors.map((c) => c.pts));

  const editing = focusTouchpointId != null || focusJourneyId != null;
  const colXOf = (jid) => (lay.columns.find((c) => c.journeyId === jid) || {}).x;
  // explicit recenter: center the edited card (touchpoint) or panel (journey), wherever it is
  const recenter = () => {
    const wrap = wrapEl.current;
    if (!wrap || typeof wrap.scrollTo !== 'function') return;
    if (focusTouchpointId != null) {
      const card = cardById[focusTouchpointId];
      if (!card) return;
      wrap.scrollTo({
        top: centerScrollTop({ cardTop: card.y, cardHeight: cardHeightOf(card.touchpoint), viewHeight: wrap.clientHeight }),
        left: centerScrollLeft({ cardCenterX: card.x, viewWidth: wrap.clientWidth, rightInset }),
        behavior: 'smooth',
      });
    } else if (focusJourneyId != null) {
      // top-align the (tall) journey panel, not center it, so recenter lands at the top
      wrap.scrollTo({
        top: journeyTopScroll(panelTop(focusJourneyId)),
        left: centerScrollLeft({ cardCenterX: colXOf(focusJourneyId), viewWidth: wrap.clientWidth, rightInset }),
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="tl-canvas-outer">
    <div className={`tl-canvas-wrap${panning ? ' panning' : ''}`} ref={wrapEl}
      onPointerDown={onPanDown} onPointerMove={onPanMove} onPointerUp={onPanEnd} onPointerCancel={onPanEnd}>
      <div className={`tl-canvas${animate ? ' tl-animate' : ''}`} style={{ width: Math.max(lay.width + 60, wrapW), height: lay.height + 60 }}>
        {lay.bands.map((b, i) => (
          <React.Fragment key={b.day}>
            <div className={`tl-band${i % 2 ? ' alt' : ''}`} style={{ top: b.top, height: b.height }} />
            <div className="tl-band-lbl" style={{ top: b.top + 8 }}>{b.label}</div>
            <div className="tl-band-sub" style={{ top: b.top + 26 }}>{b.count} msg{b.count > 1 ? 's' : ''}</div>
          </React.Fragment>
        ))}

        <div className="tl-shift" style={{ transform: `translateX(${xShift}px)` }}>
          {lay.columns.map((col) => {
            // journey focus: ring the edited journey's panel (hue-matched), dim the rest.
            // touchpoint focus: dim every panel (the ringed card carries the highlight).
            const jSel = focusJourneyId != null && col.journeyId === focusJourneyId;
            return (
              <JourneyPanel key={`panel-${col.journeyId}`} journey={journeyOf(col.journeyId)} x={col.x}
                top={panelTop(col.journeyId)} height={panelBottom(col.journeyId) - panelTop(col.journeyId)}
                hue={hueIndexOf(col.journeyId)} attributeById={attributeById} productById={productById} onEditJourney={onEditJourney}
                selected={jSel} dimmed={editing && !jSel}
                selHue={jSel ? hueOf(col.journeyId) : undefined} selGlow={jSel ? rgbaOf(hueOf(col.journeyId), 0.22) : undefined}
                innerRef={(el) => { panelEls.current[col.journeyId] = el; }} />
            );
          })}

          <svg className="tl-edges" width={lay.width + 60} height={lay.height + 60}>
            <defs>
              <marker id="tl-arrow" markerWidth="10" markerHeight="10" refX="6.5" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                <path d="M0,0 L7,4 L0,8 z" fill="#8a9bb0" />
              </marker>
            </defs>
            {/* journey spines — broken where a connector's horizontal passes over them */}
            {spines.map((s) => (
              <path key={`spine-${s.jid}`} d={gappedPath(s.pts, horizontals)} fill="none" stroke="#b8c2cc" strokeWidth="2" />
            ))}
            {/* branch connectors — verticals broken at crossings, horizontals drawn straight over */}
            {connectors.map((c) => (
              <path key={`tee-${c.jid}`} d={gappedPath(c.pts, horizontals)} fill="none" stroke="#b8c2cc" strokeWidth="2" markerEnd="url(#tl-arrow)" />
            ))}
          </svg>

          {/* Render cards in a STABLE DOM order (by touchpoint id), independent of layout
              order. Cards are absolutely positioned, so DOM order is visually irrelevant —
              but keeping it stable stops React from relocating a moved card's node (which
              would reset its CSS `top` transition and make the move snap instead of glide). */}
          {[...lay.cards].sort((a, b) => {
            const ai = a.touchpoint.placeholder ? Infinity : a.touchpoint.touchpoint_id;
            const bi = b.touchpoint.placeholder ? Infinity : b.touchpoint.touchpoint_id;
            return ai - bi;
          }).map((card) => {
            if (card.touchpoint.placeholder) {
              return (
                <div key={card.touchpoint.touchpoint_id} className="tl-card tl-card--placeholder"
                  style={{ left: card.x, top: card.y, '--hue': hueOf(card.journeyId), '--hue-soft': rgbaOf(hueOf(card.journeyId), 0.09), '--hue-soft2': rgbaOf(hueOf(card.journeyId), 0.16) }}
                  ref={(el) => { cardEls.current[card.touchpoint.touchpoint_id] = el; }}>
                  <AdvancedOnly><button type="button" className="tl-add-first" onClick={() => onAddAfter(card.journeyId, null)}>
                    <PlusOutlined /> Add first touchpoint
                  </button></AdvancedOnly>
                </div>
              );
            }
            // focus mode: touchpoint editing rings the selected card + dims the rest; journey
            // editing dims cards outside the edited journey (its panel carries the ring).
            const tpFocus = focusTouchpointId != null;
            const isSelected = tpFocus && card.touchpoint.touchpoint_id === focusTouchpointId;
            const cardDimmed = (tpFocus && !isSelected) || (focusJourneyId != null && card.journeyId !== focusJourneyId);
            const cls = `tl-card${isSelected ? ' selected' : ''}${cardDimmed ? ' dimmed' : ''}`;
            const style = isSelected
              ? { left: card.x, top: card.y, '--sel-hue': hueOf(card.journeyId), '--sel-glow': rgbaOf(hueOf(card.journeyId), 0.22) }
              : { left: card.x, top: card.y };
            return (
              <div key={card.touchpoint.touchpoint_id} className={cls} style={style}
                ref={(el) => { cardEls.current[card.touchpoint.touchpoint_id] = el; }}
                onMouseEnter={() => enterTp(card.touchpoint.touchpoint_id)} onMouseLeave={leaveTp}>
                <TouchpointNode card={card} metrics={metricsOf(card.touchpoint.touchpoint_id)} showMetrics={showMetrics} branches={branchTpIds.has(card.touchpoint.touchpoint_id)} onEdit={onEdit} />
              </div>
            );
          })}

          {/* add affordances (editing prototype): + below a card adds the next touchpoint
              (append when it's the last); + off the right edge branches a sub-journey.
              Hidden while EITHER editor drawer is open (focus set) — no adding mid-edit. */}
          {!editing && lay.cards.filter((c) => !c.touchpoint.placeholder).map((card) => {
            const ch = cardHeightOf(card.touchpoint);
            const hue = hueOf(card.journeyId);
            const hueVars = { '--hue': hue, '--hue-muted': lighten(hue, 0.5) };
            const shown = hoverTp === card.touchpoint.touchpoint_id ? ' shown' : '';
            return (
              <React.Fragment key={`add-${card.touchpoint.touchpoint_id}`}>
                <div className={`tl-add${shown}${gate}`} style={{ left: card.x, top: card.y + ch + 26, ...hueVars }} title={addTip || 'Add touchpoint after'} aria-disabled={!advanced || undefined}
                  onMouseEnter={() => enterTp(card.touchpoint.touchpoint_id)} onMouseLeave={leaveTp}
                  onClick={advanced ? () => onAddAfter(card.journeyId, card.touchpoint.touchpoint_id) : undefined}><PlusOutlined /></div>
                <div className={`tl-add branch${shown}${gate}`} style={{ left: card.x + HALF_CARD + 18, top: card.y + (showMetrics ? 90 : 48), ...hueVars }} title={addTip || 'Add branch'} aria-disabled={!advanced || undefined}
                  onMouseEnter={() => enterTp(card.touchpoint.touchpoint_id)} onMouseLeave={leaveTp}
                  onClick={advanced ? () => onAddBranch(card.touchpoint.touchpoint_id) : undefined}><PlusOutlined /></div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
      {editing && (
        <button type="button" className="tl-recenter" onClick={recenter} aria-label="Recenter on the item being edited">⌖ Recenter</button>
      )}
    </div>
  );
}
